import React from 'react';
import { Clock, CheckCircle, AlertCircle, Mail, FileText, Share2, Instagram, Facebook, Linkedin, Image } from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'blog' | 'campaign' | 'social';
  title: string;
  date: Date;
  status: string;
  platform?: string;
  imageUrl?: string;
}

interface ActivityDayViewProps {
  currentDate: Date;
  items: ActivityItem[];
}

const STATUS_CONFIG: Record<string, { label: string; icon: typeof CheckCircle; className: string }> = {
  published: { label: 'Gepubliceerd', icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  completed: { label: 'Gepubliceerd', icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  sent: { label: 'Verstuurd', icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  scheduled: { label: 'Gepland', icon: Clock, className: 'bg-sky-50 text-sky-700 ring-sky-600/20' },
  failed: { label: 'Mislukt', icon: AlertCircle, className: 'bg-red-50 text-red-700 ring-red-600/20' },
};

const TYPE_CONFIG = {
  blog: { label: 'Blog', icon: FileText, gradient: 'from-emerald-500 to-teal-600' },
  campaign: { label: 'E-mail campagne', icon: Mail, gradient: 'from-sky-500 to-blue-600' },
  social: { label: 'Social media', icon: Share2, gradient: 'from-amber-400 to-orange-500' },
};

function getPlatformIcon(platform?: string) {
  if (platform?.toLowerCase().includes('instagram')) return Instagram;
  if (platform?.toLowerCase().includes('facebook')) return Facebook;
  if (platform?.toLowerCase().includes('linkedin')) return Linkedin;
  return Share2;
}

function formatPlatformLabel(platform?: string) {
  if (!platform) return '';
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

export function ActivityDayView({ currentDate, items }: ActivityDayViewProps) {
  const dayItems = items.filter(item => {
    const itemDate = new Date(item.date);
    return itemDate.getDate() === currentDate.getDate() &&
           itemDate.getMonth() === currentDate.getMonth() &&
           itemDate.getFullYear() === currentDate.getFullYear();
  }).sort((a, b) => a.date.getTime() - b.date.getTime());

  if (dayItems.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
          <Image className="w-8 h-8 text-gray-300" />
        </div>
        <p className="text-gray-400 text-sm">
          Geen activiteit op deze dag
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {dayItems.map((item) => {
        const typeConfig = TYPE_CONFIG[item.type];
        const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.scheduled;
        const StatusIcon = statusConfig.icon;
        const PlatformIcon = item.type === 'social' ? getPlatformIcon(item.platform) : typeConfig.icon;

        return (
          <div
            key={item.id}
            className="group bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="relative aspect-[16/9] overflow-hidden">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${typeConfig.gradient} flex items-center justify-center`}>
                  <PlatformIcon className="w-12 h-12 text-white/30" />
                </div>
              )}

              <div className="absolute top-3 left-3">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset backdrop-blur-sm ${statusConfig.className}`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig.label}
                </span>
              </div>

              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-medium">
                  <typeConfig.icon className="w-3 h-3" />
                  {typeConfig.label}
                </span>
              </div>
            </div>

            <div className="p-4">
              <p className="text-xs text-gray-400 mb-1.5">
                {item.date.toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
                {item.title}
              </h3>
              {item.type === 'social' && item.platform && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <PlatformIcon className="w-3 h-3" />
                  {formatPlatformLabel(item.platform)}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
